// Standardized User type for mobile/frontend compatibility

import { z } from 'zod';
import { EXAMS } from './exam';

// Taxonomy axes: `medicalSpecialty` stays `z.string()` (SpecialtyName is
// structurally `string` — the repo deliberately doesn't hard-enum specialty).
// `forExams` mirrors the read contract (question.ts) as `z.array(z.enum(EXAMS))`
// so the inferred `ExamName[]` is preserved. These read shapes aren't parsed on
// a write path, so the enum here is a type constraint, not runtime validation.
export const UserSchema = z.object({
  userId: z.string(),
  email: z.string().nullable(),
  displayName: z.string().nullable(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  displayLastName: z.boolean().optional(),
  photoURL: z.string().nullable().optional(),
  professionalTitle: z.string().optional(),
  medicalSpecialty: z.string().optional(),
  subSpecialty: z.string().optional(),
  topicInterests: z.array(z.string()).optional(),
  forExams: z.array(z.enum(EXAMS)).optional(),
  location: z.string().optional(),
  trainingProgram: z.string().optional(),
  npiNumber: z.string().optional(), // National Provider Identifier
  examDate: z.string().optional(), // MM/DD/YYYY as written by apps/mobile/lib/dateFormat.ts, not ISO
  onboardingCompleted: z.boolean().optional(),

  // Student verification
  isStudent: z.boolean().optional(),
  eduEmail: z.string().optional(), // Secondary .edu email for verification
  studentEmailVerified: z.boolean().optional(),
  // Attests that the NUMBER EXISTS in the CMS NPPES registry as an individual provider.
  //
  // IT IS NOT AN IDENTITY CONTROL, and must never be presented as one. NPPES is fully public and
  // searchable by name, so any clinician's NPI can be looked up in seconds and typed in by anyone;
  // a borrowed NPI produces a `true` indistinguishable from a legitimate one. The registry name is
  // deliberately not compared against the submitted name — onboarding auto-fills the name FROM the
  // registry, so they match by construction. Do NOT build a "Verified clinician" badge, an access
  // gate, or any trust signal on this field. Real ownership proof needs an out-of-band channel and
  // is separate work.
  //
  // Three states, and the difference matters:
  //   absent  — no NPI supplied (students, or anyone who has not entered one)
  //   false   — supplied, but NPPES was unreachable at write time, so signup proceeded rather than
  //             blocking on a third-party outage. Healed on the next profile fetch, upgrade-only
  //             (services/npiReverification.ts).
  //   true    — supplied and confirmed to exist.
  // It is NOT a synonym for "has an NPI" — check npiNumber for that.
  npiValid: z.boolean().optional(),

  createdAt: z.string().optional(), // ISO string
  updatedAt: z.string().optional(), // ISO string
});
export type User = z.infer<typeof UserSchema>;

// User profile data returned to clients (from backend ViewData)
export const UserProfileViewDataSchema = z.object({
  userId: z.string(),
  email: z.string(),
  displayName: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  displayLastName: z.boolean().optional(),
  photoURL: z.string().nullable().optional(),
  professionalTitle: z.string().optional(),
  medicalSpecialty: z.string().optional(),
  subSpecialty: z.string().optional(),
  topicInterests: z.array(z.string()).optional(),
  forExams: z.array(z.enum(EXAMS)).optional(),
  location: z.string().optional(),
  trainingProgram: z.string().optional(),
  npiNumber: z.string().optional(), // National Provider Identifier
  examDate: z.string().optional(), // MM/DD/YYYY as written by apps/mobile/lib/dateFormat.ts, not ISO
  onboardingCompleted: z.boolean().optional(),

  // Student verification
  isStudent: z.boolean().optional(),
  eduEmail: z.string().optional(),
  studentEmailVerified: z.boolean().optional(),
  /** Number-existence attestation only — see UserSchema.npiValid. NOT an identity control. */
  npiValid: z.boolean().optional(),

  createdAt: z.string().optional(), // ISO string
  updatedAt: z.string().optional(), // ISO string
  accessLevel: z.enum(['admin', 'user']).optional(),
});
export type UserProfileViewData = z.infer<typeof UserProfileViewDataSchema>;

/**
 * Public user profile — only fields visible to other users.
 */
export const PublicUserProfileSchema = z.object({
  userId: z.string(),
  displayName: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().optional(),
  professionalTitle: z.string().optional(),
  medicalSpecialty: z.string().optional(),
  subSpecialty: z.string().optional(),
  topicInterests: z.array(z.string()).optional(),
  forExams: z.array(z.enum(EXAMS)).optional(),
  location: z.string().optional(),
  trainingProgram: z.string().optional(),
  npiNumber: z.string().optional(),
  examDate: z.string().optional(),
  photoURL: z.string().optional(),
  createdAt: z.string().optional(),
});
export type PublicUserProfile = z.infer<typeof PublicUserProfileSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Canonical editable-profile schema — the single source of truth. The backend's
// `updateProfileSchema` (routes/users.ts) is `UserProfileSchema.strip()`, not a
// copy. User taxonomy axes stay `z.string()` (NOT enums): existing users
// may hold legacy values not in the vocab, and enum-validating a re-save would
// 400. Feed alignment is fixed by sourcing the picker + `interests` collection
// from TOPICS (canonical case), not by user-side enums.
// Exam axis is `forExams` (was `preparingFor` plus a separate single `targetExam`
// joined by a backend bridge — both collapsed into `forExams` here; bridge gone).
// ─────────────────────────────────────────────────────────────────────────────
export const UserProfileSchema = z.object({
  displayName: z.string().min(1).optional(),
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
  displayLastName: z.boolean().optional(),
  professionalTitle: z.string().optional(),
  medicalSpecialty: z.string().optional(),
  subSpecialty: z.string().optional(),
  topicInterests: z.array(z.string()).optional(),
  forExams: z.array(z.string()).optional(),
  location: z.string().optional(),
  trainingProgram: z.string().optional(),
  npiNumber: z.string().optional(),
  isStudent: z.boolean().optional(),
  examDate: z.string().optional(),
});
export type UserProfile = z.infer<typeof UserProfileSchema>;
